export interface RuleSet {
  name?: string;
  active?: boolean;
  business_lines?: string[];
  [key: string]: any;
}

export const IsURL = (url: string): boolean => {
  const pattern = new RegExp(
    '^((http|https):\\/\\/)?(([a-z0-9]+([\\-\\.]{1}[a-z0-9]+)*\\.[a-z]+)|((25[0-5]|2[0-4][0-9]|[1][0-9][0-9]|[1-9][0-9]|[0-9]?)(\\.(25[0-5]|2[0-4][0-9]|[1][0-9][0-9]|[1-9][0-9]|[0-9]?)){3}(:?[0-9]*)))(\\/.*)?$',
    'i'
  );
  return pattern.test(url);
};

export const PathHelper = {
  ensureEndSlash: function (path: string): string {
    return this.hasEndSlash(path) ? path : this.addEndSlash(path);
  },
  // `path` stays `any`: legacy call sites pass possibly-undefined paths.
  ensureStartSlash: function (path: any): string {
    return this.hasStartSlash(path) ? path : this.addStartSlash(path);
  },
  ensureNoEndSlash: function (path: string): string {
    return this.hasEndSlash(path) ? this.removeLastChar(path) : path;
  },
  ensureNoStartSlash: function (path: string): string {
    return this.hasStartSlash(path) ? this.removeFirstChar(path) : path;
  },
  hasEndSlash: function (path: string): boolean {
    return path[path.length - 1] === '/';
  },
  addEndSlash: function (path: string): string {
    return path + '/';
  },
  removeLastChar: function (path: string): string {
    return path.slice(0, -1);
  },
  hasStartSlash: function (path: string): boolean {
    return path[0] === '/';
  },
  addStartSlash: function (path: string): string {
    return '/' + path;
  },
  removeFirstChar: function (path: string): string {
    return path.substring(1, path.length);
  },
};

export const getUrlVars = (search?: string | null): Record<string, string> => {
  if (!search) return {};
  return search
    .replace('?', '')
    .split('&')
    .reduce<Record<string, string>>((acc, keyVal) => {
      const [key, value] = keyVal.split('=');
      if (key) {
        acc[key] = value;
      }
      return acc;
    }, {});
};

export const getUrlDomain = (urlString: string = window.location.href): string => {
  const { hostname } = new URL(urlString);
  return hostname.substring(hostname.lastIndexOf('.', hostname.lastIndexOf('.') - 1) + 1);
};

/** gets the currentRuleSetName from the window url */
export const getCurrentRuleSetName = (currentUrl?: string): string | undefined => {
  if (currentUrl) {
    return new URLSearchParams(new URL(currentUrl).search).get('current_rule_set_name') || undefined;
  }
  if (typeof window === 'undefined') {
    return undefined;
  }
  return new URLSearchParams(window.location.search).get('current_rule_set_name') || undefined;
};

/** returns the activeRuleSet or an empty object */
// Return type is `any`: legacy call sites dereference the result directly (e.g. `.name`) even though
// this can return `{}` or undefined (when the inner `find` matches nothing).
export const getActiveRuleSetFromUrl = (ruleSets: RuleSet[] | null | undefined, currentUrl?: string): any => {
  if (!ruleSets) return {};
  const activeRuleSet = ruleSets?.find(currentRuleSet => currentRuleSet.active);
  if (activeRuleSet) {
    return activeRuleSet;
  }
  const currentRuleSetName = getCurrentRuleSetName(currentUrl);
  return currentRuleSetName
    ? ruleSets.find(ruleSet => {
        if (ruleSet.name === currentRuleSetName) {
          ruleSet.active = true;
          return ruleSet;
        }
      })
    : {};
};

/** returns the current business line using the currentRuleSetName
 *  if no valid ruleSets are provided, returns FALSE (for case where ruleSets haven't been fetched yet)
 *  returns undefined if there isn't a currentRuleSet
 */
// Return type is `any` to match the legacy inferred surface: call sites pass the result into
// `string`-typed sinks even though it can be undefined. `currentRuleSetName` accepts null (legacy call sites).
export const getCurrentBusinessLine = (
  ruleSets: RuleSet[] | null | undefined,
  currentRuleSetName?: string | null
): any => {
  if (!currentRuleSetName) {
    currentRuleSetName = getCurrentRuleSetName();
  }
  if (!currentRuleSetName) {
    return undefined;
  }
  if (!ruleSets) {
    return undefined;
  }
  const currentRuleSet = ruleSets.find(ruleSet => ruleSet.name === currentRuleSetName);
  return currentRuleSet && (currentRuleSet.business_lines || []).join(',');
};

export const loadScript = (src: string): Promise<void> =>
  new Promise((resolve, reject) => {
    try {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => {
        resolve();
      };
      script.onerror = () => {
        const failingFile = src.split('/').slice(-2).join('/');
        reject(`An issue occured while trying to load the following file: ${failingFile}`);
      };
      document.body.appendChild(script);
    } catch (e) {
      reject(e);
    }
  });
